import { Controller } from "@hotwired/stimulus";
import Axios from "axios";
import Sortable from "sortablejs";
import { flashDanger } from "../lib/flash";

// Reorder mode for a list (LI-289): a note's sub-notes, or one access group on /notes. Start adds
// .is-reordering to the element, which shows the .reorder-on parts (hint, Done, each row's grip
// and ↑/↓) and hides the .reorder-off ones; rows stop navigating, and become draggable by their
// grip (SortableJS, pointer-driven on mouse and touch alike). Every move, a drop or ↑/↓, sends the
// list's ids, in their new order, to `url` (PATCH, 204). Saves go one after another; if one fails,
// the list goes back to the last saved order and `error` is flashed.
export default class extends Controller {
  static targets = ["list", "item"];
  static values = { url: String, error: String };

  connect() {
    this.saving = Promise.resolve();
    this.sent = 0;
    this.droppedUpTo = 0;
  }

  start() {
    this.savedIds = this.ids;
    this.element.classList.add("is-reordering");
    this.links.forEach((link) => {
      link.setAttribute("tabindex", "-1");
      link.setAttribute("aria-disabled", "true");
    });
    this.refreshEnds();
    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-reorder-grip]",
      draggable: '[data-reorder-target="item"]',
      forceFallback: true,
      animation: 150,
      ghostClass: "reorder-slot",
      fallbackClass: "reorder-lifted",
      onEnd: ({ oldIndex, newIndex }) => { if (oldIndex !== newIndex) this.moved(); },
    });
    this.element.querySelector(".reorder-button--done")?.focus();
  }

  disconnect() {
    this.sortable?.destroy();
  }

  stop() {
    this.sortable?.destroy();
    this.sortable = null;
    this.element.classList.remove("is-reordering");
    this.links.forEach((link) => {
      link.removeAttribute("tabindex");
      link.removeAttribute("aria-disabled");
    });
    this.element.querySelector(".reorder-button:not(.reorder-button--done)")?.focus();
  }

  // Rows are links outside the mode; inside it a tap on one must not open the note.
  follow(event) {
    if (this.element.classList.contains("is-reordering")) event.preventDefault();
  }

  up(event) {
    const item = this.itemOf(event);
    const previous = item.previousElementSibling;
    if (!previous) return;

    previous.before(item);
    this.moved(item, "up");
  }

  down(event) {
    const item = this.itemOf(event);
    const next = item.nextElementSibling;
    if (!next) return;

    next.after(item);
    this.moved(item, "down");
  }

  // Shared by ↑/↓ and a drop: update the ends and save the new order.
  moved(item, direction) {
    this.refreshEnds();
    if (direction) this.keepFocus(item, direction);
    this.save();
  }

  // Each save is numbered; after a failure the list is put back, so every save queued behind it
  // (carrying the failed move) is dropped rather than sent.
  save() {
    const ids = this.ids;
    const number = ++this.sent;
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;

    this.saving = this.saving.then(() => {
      if (number <= this.droppedUpTo) return;

      return Axios.patch(this.urlValue, { ids }, { headers: { "X-CSRF-Token": csrf } })
        .then(() => { this.savedIds = ids; })
        .catch(() => {
          this.droppedUpTo = this.sent;
          this.restore();
        });
    });
  }

  restore() {
    const byId = new Map(this.itemTargets.map((item) => [item.dataset.id, item]));
    this.savedIds.forEach((id) => { const item = byId.get(id); if (item) this.listTarget.append(item); });
    this.refreshEnds();
    flashDanger(this.errorValue);
  }

  // ↑ on the first row and ↓ on the last are disabled.
  refreshEnds() {
    const items = this.itemTargets;
    items.forEach((item, index) => {
      this.moveButton(item, "up").disabled = index === 0;
      this.moveButton(item, "down").disabled = index === items.length - 1;
    });
  }

  // The pressed button moved with its row; when it just got disabled, focus the other one.
  keepFocus(item, direction) {
    const pressed = this.moveButton(item, direction);
    const other = this.moveButton(item, direction === "up" ? "down" : "up");
    (pressed.disabled ? other : pressed).focus();
  }

  moveButton(item, direction) {
    return item.querySelector(`[data-reorder-move="${direction}"]`);
  }

  itemOf(event) {
    return event.currentTarget.closest('[data-reorder-target="item"]');
  }

  get ids() {
    return this.itemTargets.map((item) => item.dataset.id);
  }

  get links() {
    return this.itemTargets.flatMap((item) => Array.from(item.querySelectorAll("a[href]")));
  }
}
